import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { REVIEW_DISCOVERY_LIST } from "../api/explore-queries";
import type {
  ReviewDiscoveryListQuery,
  ReviewDiscoveryListQueryVariables,
} from "../../../__generated__/graphql";

const DEFAULT_LIMIT = 10;

interface RestoredData {
  reviews: ReviewDiscoveryListQuery["reviewDiscoveryList"];
  offset: number;
  hasMore: boolean;
}

interface UseExploreQueryParams {
  selectedFilter: "all" | "nearby";
  hasLastLo: boolean;
  getLocationFromStorage: () => { lat: number; lng: number };
  restoredData: RestoredData | null;
  setAllReviews: (reviews: ReviewDiscoveryListQuery["reviewDiscoveryList"]) => void;
  setInitLoading: (value: boolean) => void;
  limit?: number;
}

export function useExploreQuery({
  selectedFilter,
  hasLastLo,
  getLocationFromStorage,
  restoredData,
  setAllReviews,
  setInitLoading,
  limit = DEFAULT_LIMIT,
}: UseExploreQueryParams) {
  const restoredOffset = restoredData?.offset ?? 0;
  const initialLimit = restoredOffset > 0 ? restoredOffset : limit;

  const queryVariables = useMemo<ReviewDiscoveryListQueryVariables>(
    () => ({
      isNearby: selectedFilter === "nearby",
      lat: hasLastLo ? getLocationFromStorage().lat : null,
      lng: hasLastLo ? getLocationFromStorage().lng : null,
      offset: 0,
      limit: initialLimit,
    }),
    [
      selectedFilter,
      hasLastLo,
      getLocationFromStorage,
      initialLimit,
    ]
  );

  const { refetch, fetchMore } = useQuery<
    ReviewDiscoveryListQuery,
    ReviewDiscoveryListQueryVariables
  >(REVIEW_DISCOVERY_LIST, {
    fetchPolicy: "cache-first",
    variables: queryVariables,
    skip: !!restoredData && (restoredData.reviews?.length ?? 0) > 0,
    onCompleted: (data) => {
      if (data?.reviewDiscoveryList && data.reviewDiscoveryList.length > 0) {
        setAllReviews(data.reviewDiscoveryList);
      } else {
        setAllReviews([]);
      }
      setInitLoading(false);
    },
  });

  return { refetch, fetchMore, queryVariables };
}
